import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, createVNode as _createVNode, unref as _unref, vShow as _vShow, withDirectives as _withDirectives, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, withCtx as _withCtx, createTextVNode as _createTextVNode, withModifiers as _withModifiers, withKeys as _withKeys, resolveDirective as _resolveDirective } from "vue"

const _hoisted_1 = { class: "llm-query-cell" }
const _hoisted_2 = { class: "llm-prompt-container" }
const _hoisted_3 = { class: "prompt-input-container" }
const _hoisted_4 = {
  class: "prompt-controls",
  style: {}
}
const _hoisted_5 = { class: "llm-prompt-text" }
const _hoisted_6 = {
  key: 0,
  class: "event-container"
}
const _hoisted_7 = { class: "events" }
const _hoisted_8 = { class: "flex align-items-center gap-2 w-full" }
const _hoisted_9 = { class: "font-bold white-space-nowrap" }
const _hoisted_10 = {
  key: 0,
  class: "query-answer"
}
const _hoisted_11 = {
  key: 1,
  class: "thinking-indicator"
}
const _hoisted_12 = { class: "thought-icon" }
const _hoisted_13 = {
  key: 2,
  class: "input-request"
}

import { ref, shallowRef, inject, computed, nextTick, onBeforeMount, getCurrentInstance, onBeforeUnmount } from "vue";
import Button from "primevue/button";
import Accordion from "primevue/accordion";
import AccordionTab from "primevue/accordiontab";
import BeakerQueryCellEvent from "./BeakerQueryCellEvent.vue";
import { BeakerQueryEvent, type BeakerQueryEventType } from "beaker-kernel/src/notebook";
import ContainedTextArea from '../misc/ContainedTextArea.vue';
import { BeakerSession } from 'beaker-kernel/src';
import { BeakerSessionComponentType } from "../session/BeakerSession.vue";
import ThinkingIcon from "../../assets/icon-components/BrainIcon.vue";


import { BeakerQueryCell } from "beaker-kernel/src";
const __default__ = {
    modelClass: BeakerQueryCell,
    icon: "pi pi-sparkles",
};


export default /*@__PURE__*/_defineComponent({
  ...__default__,
  __name: 'BeakerQueryCell',
  props: [
    'index',
    'cell',
],
  setup(__props, { expose: __expose }) {

const props = __props;

const eventIconMap = {
    "code_cell": "pi pi-code",
    "thought": "thought-icon",
    "user_answer": "pi pi-reply",
    "user_question": "pi pi-question-circle"
}

const terminalEvents = [
    "error",
    "response"
]

const enum QueryStatuses {
    NotExecuted,
    Running,
    Done,
}

const cell = shallowRef(props.cell);
const isEditing = ref<boolean>(cell.value.source === "");
const promptEditorMinHeight = ref<number>(100);
const promptText = ref<string>(cell.value.source);
const response = ref("");
const textarea = ref();
const session: BeakerSession = inject("session");
const beakerSession = inject<BeakerSessionComponentType>("beakerSession");
const instance = getCurrentInstance();


const events = computed(() => {
    return [...props.cell.events].filter((event) => event.type !== 'response');
})

const taggedCellEvents = computed(() => {
    let index = 0;
    const events: BeakerQueryEvent[] = [...props.cell.events];
    for (const queryEvent of events) {
        if (queryEvent.type == "code_cell") {
            queryEvent.content.metadata.subindex = index;
            index += 1;
        }
    }
    return events.filter((e) => !terminalEvents.includes(e.type));
});

const queryStatus = computed<QueryStatuses>(() => {
    const eventCount = events.value.length;
    if (props.cell.status === 'busy') {
        return QueryStatuses.Running;
    }
    if (eventCount === 0) {
        return QueryStatuses.NotExecuted;
    }
    else if (terminalEvents.includes(events.value[eventCount - 1].type)) {
        return QueryStatuses.Done;
    }
    else {
        return QueryStatuses.Running;
    }


})

const isLastEventTerminal = () => {
    const events: BeakerQueryEvent[] = props.cell.events;
    if (events?.length > 0) {
        return terminalEvents.includes(events[events.length - 1].type);
    }
    return false;
};

// behavior: temporarily show last event and all code cells, until terminal, in which case,
// only show code cells
const selectedEvents = computed({
    get() {
        const eventCount = events.value.length;
        if (eventCount === 0) {
            return [];
        }
        else if (eventCount === 1) {
            return [0];
        }
        else {
            return [eventCount-2, eventCount-1];
        }
    },
    set(newValue) {
        // no operation,
    },

})

const queryEventNameMap: {[eventType in BeakerQueryEventType]: string} = {
    "thought": "Thought",
    "response": "Final Response",
    "code_cell": "Code",
    "user_answer": "Answer",
    "user_question": "Question",
    "error": "Error",
    "abort": "Abort"
}

const promptDoubleClick = (event) => {
    if (!isEditing.value) {
        promptEditorMinHeight.value = (event.target as HTMLElement).clientHeight;
        isEditing.value = true;
    }
}

const respond = () => {
    if (!response.value.trim()) {
        return; // Do nothing unless the reply has contents
    }
    props.cell.respond(response.value, session);
    response.value = "";
};

function execute() {
    cell.value.source = promptText.value;
    isEditing.value = false;
    nextTick(() => {
        const future = props.cell.execute(session);
        // Add reference to cell for downstream processing.
        future.registerMessageHook(
            (msg) => {
                msg.cell = cell.value;
            }
        )
    });
}

function enter() {
    if (!isEditing.value) {
        isEditing.value = true;
    }
    nextTick(() => {
        textarea.value?.$el?.focus();
    });
}

function exit() {
    textarea.value?.$el?.blur();
}

function clear() {
    cell.value.source = "";
    isEditing.value = true;
    promptEditorMinHeight.value = 100;
    promptText.value = "";
    response.value = "";
}

__expose({
    execute,
    enter,
    exit,
    clear,
    cell,
});

onBeforeMount(() => {
    beakerSession.cellRegistry[cell.value.id] = instance.vnode;
});

onBeforeUnmount(() => {
    delete beakerSession.cellRegistry[cell.value.id];
});


return (_ctx: any,_cache: any) => {
  const _directive_focustrap = _resolveDirective("focustrap")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      class: "query",
      onDblclick: promptDoubleClick
    }, [
      _cache[6] || (_cache[6] = _createElementVNode("div", { class: "query-steps" }, "User Query:", -1)),
      _createElementVNode("div", _hoisted_2, [
        _withDirectives(_createElementVNode("div", _hoisted_3, [
          _createVNode(ContainedTextArea, {
            ref_key: "textarea",
            ref: textarea,
            class: "prompt-input",
            modelValue: promptText.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((promptText).value = $event)),
            style: _normalizeStyle({minHeight: `${promptEditorMinHeight.value}px`})
          }, null, 8, ["modelValue", "style"]),
          _createElementVNode("div", _hoisted_4, [
            _createVNode(_unref(Button), {
              label: "Submit",
              onClick: execute
            }),
            _createVNode(_unref(Button), {
              label: "Cancel",
              onClick: _cache[1] || (_cache[1] = ($event: any) => {promptText.value = cell.value.source; isEditing.value = false})
            })
          ])
        ], 512), [
          [_vShow, isEditing.value]
        ]),
        _withDirectives(_createElementVNode("div", null, [
          _createElementVNode("div", _hoisted_5, _toDisplayString(cell.value.source), 1)
        ], 512), [
          [_vShow, !isEditing.value]
        ])
      ])
    ], 32),
    (events.value.length > 0)
      ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
          _createElementVNode("div", _hoisted_7, [
            _cache[8] || (_cache[8] = _createElementVNode("div", { class: "query-steps" }, "Agent actions:", -1)),
            _createVNode(_unref(Accordion), {
              multiple: true,
              class: _normalizeClass('query-accordion'),
              "active-index": selectedEvents.value,
              "onUpdate:activeIndex": _cache[2] || (_cache[2] = ($event: any) => ((selectedEvents).value = $event))
            }, {
              default: _withCtx(() => [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(events.value, (event, eventIndex) => {
                  return (_openBlock(), _createBlock(_unref(AccordionTab), {
                    key: eventIndex,
                    pt: {
                            header: {
                                class: [`query-tab`, `query-tab-${event.type}`]
                            },
                            headerAction: {
                                class: [`query-tab-headeraction`, `query-tab-headeraction-${event.type}`]
                            },
                            content: {
                                class: [`query-tab-content-${event.type}`]
                            },
                            headerIcon: {
                                class: [`query-tab-icon-${event.type}`]
                            }
                        }
                  }, {
                    header: _withCtx(() => [
                      _createElementVNode("span", _hoisted_8, [
                        _createElementVNode("span", {
                          class: _normalizeClass(eventIconMap[event.type])
                        }, [
                          (event.type === 'thought')
                            ? (_openBlock(), _createBlock(ThinkingIcon, {
                                key: 0,
                                class: "thought-icon"
                              }))
                            : _createCommentVNode("", true)
                        ], 2),
                        _createElementVNode("span", _hoisted_9, _toDisplayString(queryEventNameMap[event.type]), 1)
                      ])
                    ]),
                    default: _withCtx(() => [
                      (_openBlock(), _createBlock(BeakerQueryCellEvent, {
                        key: eventIndex,
                        event: event,
                        parentQueryCell: cell.value
                      }, null, 8, ["event", "parentQueryCell"]))
                    ]),
                    _: 2
                  }, 1032, ["pt"]))
                }), 128))
              ]),
              _: 1
            }, 8, ["active-index"]),
            (isLastEventTerminal())
              ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                  _cache[7] || (_cache[7] = _createElementVNode("h3", { class: "query-steps" }, "Result", -1)),
                  (isLastEventTerminal())
                    ? (_openBlock(), _createBlock(BeakerQueryCellEvent, {
                        key: 0,
                        event: cell.value?.events[cell.value?.events.length - 1],
                        "parent-query-cell": cell.value
                      }, null, 8, ["event", "parent-query-cell"]))
                    : _createCommentVNode("", true)
                ]))
              : _createCommentVNode("", true)
          ])
        ]))
      : _createCommentVNode("", true),
    (cell.value.status === 'busy')
      ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
          _createElementVNode("span", _hoisted_12, [
            _createVNode(ThinkingIcon)
          ]),
          _cache[9] || (_cache[9] = _createTextVNode(" Thinking ")),
          _cache[10] || (_cache[10] = _createElementVNode("span", { class: "thinking-animation" }, null, -1))
        ]))
      : _createCommentVNode("", true),
    (cell.value.status === 'awaiting_input')
      ? _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_13, [
          _createVNode(ContainedTextArea, {
            style: {"margin-right":"0.75rem","flex":"1"},
            autoFocus: "",
            placeholder: "Enter your response here",
            modelValue: response.value,
            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((response).value = $event)),
            onKeydown: [
              _cache[4] || (_cache[4] = _withKeys(_withModifiers(() => {}, ["ctrl","stop"]), ["enter"])),
              _cache[5] || (_cache[5] = _withKeys(_withModifiers(() => {}, ["shift","stop"]), ["enter"]))
            ],
            onSubmit: respond
          }, null, 8, ["modelValue"]),
          _cache[11] || (_cache[11] = _createTextVNode("   ")),
          _createVNode(_unref(Button), {
            outlined: "",
            severity: "success",
            size: "small",
            label: "reply",
            onClick: respond
          })
        ])), [
          [_directive_focustrap]
        ])
      : _createCommentVNode("", true)
  ]))
}
}

})